import { designVariables as tk } from "@studio3t/design-variables";
import { Icon, IconSVG, SVGColorKey } from "@studio3t/generic-components";
import type React from "react";
import { type KeyboardEvent, useCallback, useRef } from "react";
import styled from "styled-components";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onAbort?: () => void;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Without its own frame (padding and top border): the host provides
   *  one, as the Agent page's footer card does. */
  bare?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSubmit,
  onAbort,
  isLoading = false,
  placeholder = "Ask about your transformation script",
  disabled = false,
  bare = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isLoading && value.trim()) {
          onSubmit();
        }
      }
    },
    [isLoading, value, onSubmit],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${Math.min(
          textareaRef.current.scrollHeight,
          150,
        )}px`;
      }
    },
    [onChange],
  );

  const handleSubmitClick = useCallback(() => {
    if (isLoading && onAbort) {
      onAbort();
    } else if (value.trim()) {
      onSubmit();
    }
  }, [isLoading, onAbort, value, onSubmit]);

  return (
    <Container $bare={bare}>
      <InputRow>
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          // Stays enabled while a reply streams: disabling it would drop
          // the focus, and Enter is ignored until the reply is done anyway.
          disabled={disabled}
          rows={1}
          data-testid="ai-chat-input"
        />
        <SubmitButton
          onClick={handleSubmitClick}
          disabled={disabled || (!isLoading && !value.trim())}
          $isLoading={isLoading}
          data-testid="ai-submit-button"
          title={isLoading ? "Stop generating" : "Send message"}
        >
          <IconSVG
            icon={isLoading ? Icon.Stop : Icon.Right}
            size={16}
            svgColorKey={
              isLoading ? SVGColorKey.errorIcon : SVGColorKey.semanticTextBody
            }
          />
        </SubmitButton>
      </InputRow>
      <HintText>[Enter] send | [Shift+Enter] new line</HintText>
    </Container>
  );
};

const Container = styled.div<{ $bare: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${tk.ui.input.gapsupportive}px;
  flex-shrink: 0;
  ${(p) =>
    p.$bare
      ? ""
      : `
    padding: ${tk.ui.semantic.spacings}px ${tk.ui.semantic.spacingm}px
      ${tk.ui.semantic.spacingm}px;
    border-top: 1px solid ${p.theme.cards1Border};
    border-radius: 0 0 6px 6px;
  `}
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;

const Textarea = styled.textarea`
  flex: 1;
  min-height: 32px;
  max-height: 150px;
  padding: 7px 4px;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${(p) => p.theme.inputDefaultBorder};
  border-radius: 0;
  color: ${(p) => p.theme.semanticTextBody};
  font-family: inherit;
  font-size: 14px;
  line-height: 1.2;
  resize: none;
  outline: none;
  transition: border-color 0.15s ease;

  &::placeholder {
    color: ${(p) => p.theme.semanticTextPlaceholder};
  }

  &:focus {
    border-bottom-color: ${(p) => p.theme.buttonPrimaryDefaultBackground};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button<{ $isLoading: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  flex-shrink: 0;
  background: ${(p) =>
    p.$isLoading ? p.theme.badgeErrorSubtleBackground : "transparent"};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition:
    background 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$isLoading
        ? p.theme.badgeErrorSubtleBackground
        : p.theme.cards2Background};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const HintText = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${(p) => p.theme.semanticTextBody};
`;

export default ChatInput;
